import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { IFaIcon } from 'src/app/api/modules/icons/fa/IFaIcon';
import { IIconState } from 'src/app/api/modules/icons/IIconState';
import { FaIconComponent } from '../fa-icon/fa-icon.component';

/**
 * IconState shows a single Icon from a list of icons as a state container.
 *
 * For example imagine you have an icon "lights-on" and "lights-off". These are
 * provided as states in the config; the `state` input selects which one is
 * shown (e.g. `[state]="'off'"` shows the icon whose state is "off").
 */
@Component({
  selector: 'app-icon-state',
  templateUrl: './icon-state.component.html',
  styleUrls: ['./icon-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent],
})
export class IconStateComponent {
  /**
   * The Config as provided to the element.
   */
  readonly config = input<IIconState | null>();

  /**
   * The name of the state to show.
   */
  readonly state = input<string | null>('default');

  /**
   * The icon for the requested state.
   *
   * Falls back to the state named 'default' when the requested state doesn't
   * exist; renders nothing while unconfigured or when neither state matches.
   */
  protected readonly selectedIcon = computed<IFaIcon | undefined>(() => {
    const states = this.config()?.states;
    if (!states?.length) {
      return undefined;
    }
    const name = this.state() ?? 'default';
    return states.find((icon) => icon.state === name) ?? states.find((icon) => icon.state === 'default');
  });
}
